import React, {
  type ChangeEvent,
  type ComponentProps,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import { cn } from "../../../lib/utils.ts";
import {
  ContactAutocompleteDropdown,
  type ContactDropdownPosition,
} from "../../ui/contact-autocomplete.tsx";
import { SlashCommandDropdown } from "../../ui/slash-command-dropdown.tsx";
import { GuidedFlowDropdown } from "../../typeahead/guided-flow-dropdown.tsx";
import { CONTEXT_SUGGESTIONS, useGuidedFlowIntegration } from "./useGuidedFlowIntegration.ts";
import { UI_CONSTANTS } from "./constants.ts";
import { useSlashSystem } from "./useSlashSystem.ts";
import { useMentionSystem } from "./useMentionSystem.ts";

const DEFAULT_MAX_LINES_CLASS_NAME = cn(
  UI_CONSTANTS.MAX_LINES_MOBILE,
  UI_CONSTANTS.MAX_LINES_DESKTOP,
);

/** Module-level cached canvas for text measurement (never appended to DOM). */
let measureCanvas: HTMLCanvasElement | null = null;

interface SmartTextareaProps extends Omit<
  ComponentProps<"textarea">,
  "onChange" | "value" | "onKeyDown" | "onPaste"
> {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>, hasPopupOpen: boolean) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  className?: string;
  initialRows?: number;
  /**
   * Stop growing after this many lines and scroll instead.
   *
   * The shared `MAX_LINES_*` utilities already impose a ceiling — 5 lines on
   * mobile, 9 on desktop — but they are classes, and a caller that needs a
   * different number cannot reliably outrank them: the desktop cap rides an
   * `@md:` variant *and* an `@supports` query, which `tailwind-merge` does not
   * treat as conflicting with a plain `max-h-*`. An inline `max-height` beats
   * all of them without an `!important` arms race, so the cap is applied here
   * rather than handed over as a class.
   */
  maxRows?: number;
  /**
   * Replaces the shared `MAX_LINES_*` ceiling for a caller whose cap differs per
   * breakpoint, which a single `maxRows` cannot say. Replaced rather than merged
   * for the reason `maxRows` gives: `tailwind-merge` would keep both caps.
   */
  maxLinesClassName?: string;
  /**
   * The value this mounts with is text the user typed, not a prefill. A value
   * present at mount otherwise counts as a prefill, which the guided flow
   * expands (appending " with " and opening its picker).
   */
  initialValueIsTyped?: boolean;
}

export const SmartTextarea = forwardRef<HTMLTextAreaElement, SmartTextareaProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      onPaste,
      className,
      initialRows = 1,
      maxRows,
      maxLinesClassName = DEFAULT_MAX_LINES_CLASS_NAME,
      initialValueIsTyped,
      onScroll,
      ...props
    },
    forwardedRef,
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    // At most one overlay renders at a time, so one ref serves all three.
    const overlayRef = useRef<HTMLDivElement>(null);

    // Expose the textarea ref to parent
    useImperativeHandle(forwardedRef, () => textareaRef.current as HTMLTextAreaElement);

    // Track when value changes originate from user typing (vs external prefill).
    const isUserInputRef = useRef(false);

    // Track cursor-position setTimeout IDs for cleanup on unmount
    const cursorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
      return () => {
        if (cursorTimeoutRef.current) clearTimeout(cursorTimeoutRef.current);
      };
    }, []);

    // Auto-resize textarea based on content
    // biome-ignore lint/correctness/useExhaustiveDependencies: value change should trigger this effect
    useLayoutEffect(() => {
      const el = textareaRef.current;
      if (!el) return;
      // Measured from a collapsed box, or `scrollHeight` only ever reports the
      // height it already has and the field can never shrink back.
      el.style.height = "auto";
      if (maxRows === undefined) {
        el.style.height = `${el.scrollHeight}px`;
        el.style.maxHeight = "";
        return;
      }
      // `line-height` computes to "normal" unless something sets it, which
      // parses as NaN — falling back to the 20px this composer's type scale
      // produces beats collapsing the field to nothing.
      const lineHeight = Number.parseFloat(globalThis.getComputedStyle(el).lineHeight) || 20;
      const ceiling = lineHeight * maxRows;
      el.style.maxHeight = `${ceiling}px`;
      el.style.height = `${Math.min(el.scrollHeight, ceiling)}px`;
    }, [value, maxRows]);

    // An overlay paints the text the transparent textarea holds, so it has to
    // show the same slice of it. Unsynced, a textarea scrolled past its cap left
    // the overlay showing the top of the message while the caret was at the end.
    const syncOverlayScroll = useCallback(() => {
      const overlay = overlayRef.current;
      const textarea = textareaRef.current;
      if (overlay && textarea) overlay.scrollTop = textarea.scrollTop;
    }, []);
    // Every render, not just on scroll: an overlay that mounts over an
    // already-scrolled textarea (an @mention typed at the end of a long message)
    // starts at the top, and nothing scrolls to tell it otherwise.
    useLayoutEffect(syncOverlayScroll);

    // ─── Dropdown positioning ─────────────────────────────────────────────

    const getDropdownPosition = useCallback(
      (charOffset = 0, text?: string): ContactDropdownPosition | null => {
        if (!textareaRef.current) return null;
        const textareaRect = textareaRef.current.getBoundingClientRect();
        const textareaLeft = textareaRect.left + window.scrollX;
        const textareaRight = textareaRect.right + window.scrollX;
        const dropdownWidth = 300;

        let pixelOffset = 0;
        if (charOffset > 0) {
          const textBefore = (text ?? textareaRef.current.value ?? "").slice(0, charOffset);
          const font = globalThis.getComputedStyle(textareaRef.current).font;
          measureCanvas ??= document.createElement("canvas");
          const ctx = measureCanvas.getContext("2d");
          if (ctx && font) {
            ctx.font = font;
            pixelOffset = ctx.measureText(textBefore).width;
          }
        }

        let left = textareaLeft + pixelOffset;
        left = Math.max(textareaLeft, left);
        left = Math.min(left, textareaRight - dropdownWidth);

        // Menus open above the box, where the composer always has room. A box
        // near the top of the screen — a form answer in the side panel — has
        // none, so `top` names the spot below it for the menu to fall back to.
        return {
          top: textareaRect.bottom + window.scrollY + 8,
          left,
          width: textareaRect.width,
          anchorBottom: textareaRect.top + window.scrollY,
        };
      },
      [],
    );

    // ─── Hook up the three systems ────────────────────────────────────────

    const guidedFlowIntegration = useGuidedFlowIntegration({
      value,
      onChange,
      textareaRef,
      isUserInputRef,
      initialValueIsTyped,
    });

    const {
      guidedFlow,
      guidedFlowOverlaySegments,
      hasGuidedFlowOverlay,
      guidedFlowComposedText,
      guidedFlowCharOffset,
      isPastingRef,
    } = guidedFlowIntegration;

    const slash = useSlashSystem({
      onChange,
      getDropdownPosition,
      cursorTimeoutRef,
      textareaRef,
    });

    const mention = useMentionSystem({
      value,
      onChange,
      getDropdownPosition,
      cursorTimeoutRef,
      textareaRef,
      isUserInputRef,
    });

    // Clear resolved mentions when guided flow prefill takes over
    useEffect(() => {
      if (guidedFlowIntegration.isOpen) {
        mention.clearResolved();
      }
    }, [guidedFlowIntegration.isOpen, mention.clearResolved]);

    // ─── Input routing ────────────────────────────────────────────────────

    const guidedFlowStep = guidedFlow.state.step;
    const guidedFlowIsActive = guidedFlow.isActive;
    const guidedFlowHandleInput = guidedFlow.handleInputChange;
    const guidedFlowReset = guidedFlow.reset;

    const handleInputChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        isUserInputRef.current = true;
        onChange(newValue);

        if (guidedFlowStep === "sent") return;

        const cursorPos = e.target.selectionStart;
        const textBeforeCursor = newValue.slice(0, cursorPos);

        // Priority 1: slash commands
        if (slash.onInputChange(newValue, textBeforeCursor)) {
          mention.close();
          if (guidedFlowIsActive) guidedFlowReset();
          return;
        }

        // Priority 2: @ mentions
        if (mention.onInputChange(textBeforeCursor)) {
          if (guidedFlowIsActive) guidedFlowReset();
          return;
        }

        // Priority 3: guided flow
        if (!newValue.trim() && guidedFlowIsActive) {
          guidedFlowReset();
          return;
        }

        if (!isPastingRef.current) {
          guidedFlowHandleInput(newValue, cursorPos);
        }
      },
      [
        onChange,
        slash.onInputChange,
        mention.onInputChange,
        mention.close,
        guidedFlowStep,
        guidedFlowIsActive,
        guidedFlowReset,
        guidedFlowHandleInput,
        isPastingRef,
      ],
    );

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        isPastingRef.current = true;
        onPaste?.(e);
        requestAnimationFrame(() => {
          isPastingRef.current = false;
        });
      },
      [onPaste, isPastingRef],
    );

    // ─── Keyboard routing ─────────────────────────────────────────────────

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const hasPopupOpen = slash.isOpen || mention.isOpen || guidedFlowIntegration.isOpen;

        // Priority: slash → mention → guided flow (first true wins)
        if (slash.onKeyDown(e)) return;
        if (mention.onKeyDown(e)) return;
        if (guidedFlowIntegration.onKeyDown(e)) return;

        onKeyDown?.(e, hasPopupOpen);
      },
      [
        slash.isOpen,
        slash.onKeyDown,
        mention.isOpen,
        mention.onKeyDown,
        guidedFlowIntegration.isOpen,
        guidedFlowIntegration.onKeyDown,
        onKeyDown,
      ],
    );

    // ─── Render ───────────────────────────────────────────────────────────

    // The overlay sits over the textarea's box and scrolls in step with it, so
    // `overflow-hidden` is what keeps a message taller than the box inside it:
    // without the clip it painted on past the composer's edge and over whatever
    // sat below. `inset-0` already sizes it to that box; a line cap of its own
    // would cut it short of a textarea whose `maxRows` reaches past the shared one.
    const overlayClassName = cn(
      "absolute inset-0 overflow-hidden pointer-events-none whitespace-pre-wrap overflow-wrap-anywhere",
      "font-normal text-base",
    );

    return (
      <>
        <div className="relative grow min-w-0">
          {/* Mention overlay — highlights @query with underline treatment */}
          {mention.hasQueryOverlay && (
            <div ref={overlayRef} className={overlayClassName} aria-hidden="true">
              <span className="text-popover-foreground">
                {value.slice(0, mention.startPos ?? 0)}
              </span>
              <span className="text-popover-foreground underline decoration-muted-foreground/30 underline-offset-2">
                @{mention.query}
              </span>
              <span className="text-popover-foreground">
                {value.slice((mention.startPos ?? 0) + 1 + mention.query.length)}
              </span>
            </div>
          )}
          <textarea
            className={cn(
              "relative bg-inherit",
              "w-full resize-none border-none",
              "focus:outline-hidden focus:border-none",
              "placeholder-muted-foreground/60 font-normal text-base",
              mention.hasQueryOverlay || hasGuidedFlowOverlay || mention.hasLinkOverlay
                ? "text-transparent caret-popover-foreground"
                : "text-popover-foreground",
              maxLinesClassName,
              "disabled:cursor-not-allowed disabled:opacity-50",
              "p-0",
              className,
            )}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onScroll={(e) => {
              syncOverlayScroll();
              onScroll?.(e);
            }}
            ref={textareaRef}
            rows={initialRows}
            value={value}
            onChange={handleInputChange}
            tabIndex={0}
            aria-label="Message input"
            {...props}
          />
          {/* Guided flow overlay */}
          {hasGuidedFlowOverlay && !mention.hasQueryOverlay && (
            <div ref={overlayRef} className={overlayClassName} aria-hidden="true">
              {guidedFlowOverlaySegments.map((seg, i) =>
                seg.step ? (
                  <span
                    key={`${seg.step}-${i}`}
                    className="text-popover-foreground underline decoration-muted-foreground/30 underline-offset-2 pointer-events-auto cursor-pointer hover:decoration-muted-foreground/60"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      guidedFlow.jumpToStep(seg.step!);
                    }}
                  >
                    {seg.text}
                  </span>
                ) : (
                  <span key={`plain-${i}`} className="text-popover-foreground">
                    {seg.text}
                  </span>
                ),
              )}
            </div>
          )}
          {/* Resolved mention overlay — clickable underlined links */}
          {mention.hasLinkOverlay && !mention.hasQueryOverlay && !hasGuidedFlowOverlay && (
            <div ref={overlayRef} className={overlayClassName} aria-hidden="true">
              {mention.overlaySegments.map((seg, i) =>
                seg.mentionIndex !== undefined ? (
                  <span
                    key={`mention-${seg.mentionIndex}-${i}`}
                    className="text-popover-foreground underline decoration-muted-foreground/30 underline-offset-2 pointer-events-auto cursor-pointer hover:decoration-muted-foreground/60"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      mention.handleSegmentClick(seg.mentionIndex!);
                    }}
                  >
                    {seg.text}
                  </span>
                ) : (
                  <span key={`plain-${i}`} className="text-popover-foreground">
                    {seg.text}
                  </span>
                ),
              )}
            </div>
          )}
        </div>

        {mention.dropdownPos && (mention.query || mention.editingMentionIndex !== null) && (
          <ContactAutocompleteDropdown
            ref={mention.dropdownRef}
            query={mention.query}
            position={mention.dropdownPos}
            onSelect={mention.handleContactSelect}
            onSelectHandle={mention.handleHandleSelect}
            onSelectEmail={mention.handleEmailSelect}
            onNoMatches={mention.handleNoMatches}
            filterHandleTypes={["emailAddress", "phoneNumber"]}
            showAllWhenEmpty={mention.editingMentionIndex !== null}
            selectedContactIds={
              mention.editingMentionRef.current?.contactId
                ? new Set([mention.editingMentionRef.current.contactId])
                : undefined
            }
          />
        )}

        {slash.query !== null && slash.dropdownPos && (
          <SlashCommandDropdown
            ref={slash.dropdownRef}
            query={slash.query}
            position={slash.dropdownPos}
            onSelect={slash.handleSelect}
            onSelectScheduling={slash.handleGuidedFlowTrigger}
          />
        )}

        {guidedFlowIntegration.isOpen &&
          (() => {
            const pos = getDropdownPosition(guidedFlowCharOffset, guidedFlowComposedText);
            if (!pos) return null;
            return (
              <GuidedFlowDropdown
                flow={guidedFlow}
                position={pos}
                contextSuggestions={CONTEXT_SUGGESTIONS[guidedFlow.state.triggerWord ?? ""] ?? []}
              />
            );
          })()}
      </>
    );
  },
);

SmartTextarea.displayName = "SmartTextarea";
